// WCAG 2.1 contrast ratio utilities for the admin Appearance editor.
//
// We surface the ratio + a pass/fail badge next to color inputs so the
// merchant SEES immediately when a chosen text/background pair fails
// the 4.5:1 (normal text) or 3:1 (large text / UI) thresholds. This is
// what Lighthouse "Background and foreground colors do not have a
// sufficient contrast ratio" flags on the storefront.

function parseColor(input: string): { r: number; g: number; b: number } | null {
  if (!input) return null;
  const s = input.trim();
  const rgbMatch = s.match(/^rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/i);
  if (rgbMatch) {
    return {
      r: Math.max(0, Math.min(255, parseInt(rgbMatch[1], 10))),
      g: Math.max(0, Math.min(255, parseInt(rgbMatch[2], 10))),
      b: Math.max(0, Math.min(255, parseInt(rgbMatch[3], 10))),
    };
  }
  let h = s.replace(/^#/, "");
  if (h.length === 3) h = h.split("").map((c) => c + c).join("");
  if (h.length !== 6 || !/^[0-9a-fA-F]{6}$/.test(h)) return null;
  return {
    r: parseInt(h.slice(0, 2), 16),
    g: parseInt(h.slice(2, 4), 16),
    b: parseInt(h.slice(4, 6), 16),
  };
}

const parseHex = parseColor;

function srgbToLinear(c: number): number {
  const v = c / 255;
  return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
}

function relativeLuminance({ r, g, b }: { r: number; g: number; b: number }): number {
  return 0.2126 * srgbToLinear(r) + 0.7152 * srgbToLinear(g) + 0.0722 * srgbToLinear(b);
}

/** Returns the WCAG contrast ratio (1..21) or null if either color is invalid. */
export function wcagContrastRatio(fg: string, bg: string): number | null {
  const a = parseHex(fg);
  const b = parseHex(bg);
  if (!a || !b) return null;
  const la = relativeLuminance(a);
  const lb = relativeLuminance(b);
  const [hi, lo] = la > lb ? [la, lb] : [lb, la];
  return (hi + 0.05) / (lo + 0.05);
}

export type ContrastGrade = "AAA" | "AA" | "AA-large" | "fail";

export function gradeContrast(ratio: number | null): ContrastGrade | null {
  if (ratio == null) return null;
  if (ratio >= 7) return "AAA";
  if (ratio >= 4.5) return "AA";
  if (ratio >= 3) return "AA-large";
  return "fail";
}

/**
 * Returns a readable foreground color ("#ffffff" or "#000000") for the given
 * background, picking whichever yields the higher WCAG contrast ratio. Used
 * by tab/pill controls where the merchant's brand color may be mid-luma
 * (e.g. rgb(186,109,109)) and white text fails AA — flagged by PageSpeed
 * "Background and foreground colors do not have a sufficient contrast ratio"
 * (May 2026, luxurygive.com).
 */
export function pickReadableTextColor(bg: string, opts?: { dark?: string; light?: string }): string {
  const dark = opts?.dark ?? "#000000";
  const light = opts?.light ?? "#ffffff";
  const parsed = parseColor(bg);
  if (!parsed) return light;
  const lum = relativeLuminance(parsed);
  const contrastWithWhite = (1.05) / (lum + 0.05);
  const contrastWithBlack = (lum + 0.05) / 0.05;
  return contrastWithWhite >= contrastWithBlack ? light : dark;
}
